import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { get, patch } from "../../api/client.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../hooks/usePermissions.js";
import { downloadCsv } from "../../utils/csvExport.js";
import { parseCsv, type ParseResult } from "./parseCsv.js";
import {
  buildMismatchCsv,
  compareCrmWithFulfilment,
  comparisonToCsv,
  escapeCsvValue,
  isBasicStandardChange,
  parseCrmCsv,
  parseFulfilmentCsv,
  type ComparisonResult,
  type FulfilmentRow,
} from "./compareCrm.js";
import styles from "./BulkSubscriptionUpdate.module.css";

type Phase = "idle" | "previewing" | "mapping" | "previewed" | "running" | "done";

const MAPPABLE_FIELDS = [
  {
    key: "startDate",
    label: "Start date",
    hint: "YYYY-MM-DD",
    currentHeaders: ["fulfilment_start_date"],
  },
  {
    key: "endDate",
    label: "End date",
    hint: "YYYY-MM-DD",
    currentHeaders: ["fulfilment_end_date"],
  },
  {
    key: "siteStructureId",
    label: "Region",
    hint: "e.g. UK",
    currentHeaders: ["fulfilment_site_structure_id", "country_fulfilment", "country_name"],
  },
  {
    key: "productCode",
    label: "Product code",
    hint: "e.g. SUBSCRIPTION-STANDARD",
    currentHeaders: ["product_fulfilment", "product_name"],
  },
] as const;

type MappableFieldKey = (typeof MAPPABLE_FIELDS)[number]["key"];

type ColumnMapping = Record<MappableFieldKey, number | null>;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

type DateValidationError = {
  subscriptionId: string;
  errors: string[];
};

type SubscriptionUpdate = {
  subscriptionId: string;
  startDate?: string;
  endDate?: string;
  siteStructureId?: string;
  productCode?: string;
};

type BulkUpdateResponse = {
  updated: number;
  requestId: string;
  unmatchedSubscriptionIds?: string[];
};

type ExportFilterOptions = {
  regions: string[];
  countries: string[];
  productCodes: string[];
  statuses: string[];
};

type CompareFilters = {
  region: string[];
  countryName: string[];
  productCode: string[];
  status: string[];
};

function autoDetectColumn(headers: string[], target: string): number | null {
  const normalised = headers.map(h => h.toLowerCase().replace(/[\s_-]+/g, ""));
  const targetNorm = target.toLowerCase().replace(/[\s_-]+/g, "");
  const idx = normalised.indexOf(targetNorm);
  return idx !== -1 ? idx : null;
}

function buildAutoMapping(headers: string[]): ColumnMapping {
  return {
    startDate: autoDetectColumn(headers, "start_date"),
    endDate: autoDetectColumn(headers, "end_date"),
    siteStructureId: autoDetectColumn(headers, "site_structure_id"),
    productCode:
      autoDetectColumn(headers, "product_code") ?? autoDetectColumn(headers, "product_fulfilment"),
  };
}

const VALID_SCOPES = new Set(["GLOBAL", "EMEA", "APAC", "UK", "LATAM", "CARIBBEAN"]);

const REGION_ALIASES: Record<string, string> = {
  usa: "United States",
  "uk (england)": "England",
};

export function resolveRegionDisplay(value: string, countries: string[] | undefined): string {
  if (!value) return "";
  if (VALID_SCOPES.has(value) || /^[0-9]+$/.test(value)) return value;
  const lower = value.toLowerCase();
  const alias = REGION_ALIASES[lower];
  if (countries) {
    const match = countries.find(c => c.toLowerCase() === (alias?.toLowerCase() ?? lower));
    if (match) return match;
  }
  return alias ?? value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function hasMappedField(mapping: ColumnMapping): boolean {
  return Object.values(mapping).some(v => v !== null);
}

function buildUpdates(parseResult: ParseResult, mapping: ColumnMapping): SubscriptionUpdate[] {
  return parseResult.rows
    .filter(r => r.valid)
    .map(row => {
      const update: SubscriptionUpdate = { subscriptionId: row.subscriptionId };
      for (const field of MAPPABLE_FIELDS) {
        const colIdx = mapping[field.key];
        if (colIdx !== null) {
          const value = row.cells[colIdx]?.trim();
          if (value) {
            update[field.key] = value;
          }
        }
      }
      return update;
    })
    .filter(u => u.startDate || u.endDate || u.siteStructureId || u.productCode);
}

async function executeBulkUpdate(
  updates: SubscriptionUpdate[],
  onProgress: (done: number, total: number) => void,
): Promise<{ updated: number; errors: string[]; notFound: string[] }> {
  const chunkSize = 100;
  let totalUpdated = 0;
  const errors: string[] = [];
  const notFound: string[] = [];

  for (let i = 0; i < updates.length; i += chunkSize) {
    const chunk = updates.slice(i, i + chunkSize);
    try {
      const data = await patch<BulkUpdateResponse>("/v1/subscriptions/bulk-update", {
        updates: chunk,
      });
      totalUpdated += data.updated;
      if (data.unmatchedSubscriptionIds) {
        notFound.push(...data.unmatchedSubscriptionIds);
      }
    } catch (err) {
      const status = err instanceof Error ? (err as Error & { status?: number }).status : undefined;
      if (status === 401 || status === 403) {
        errors.push("Session expired — please log in again");
        onProgress(updates.length, updates.length);
        return { updated: totalUpdated, errors, notFound };
      }
      const message = err instanceof Error ? err.message : String(err);
      errors.push(`Chunk ${Math.floor(i / chunkSize) + 1}: ${message}`);
    }
    onProgress(Math.min(i + chunkSize, updates.length), updates.length);
  }

  return { updated: totalUpdated, errors, notFound };
}

function fulfilmentToCsv(rows: FulfilmentRow[]): string {
  const headers = [
    "subscription_id",
    "company_name",
    "crm_account_id",
    "product_code",
    "product_name",
    "country_name",
    "region",
    "start_date",
    "end_date",
    "status",
    "order_id",
    "migration_source",
  ];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.subscriptionId,
        r.companyName ?? "",
        r.crmAccountId ?? "",
        r.productCode,
        r.productName,
        r.countryName ?? "",
        r.region ?? "",
        r.startDate,
        r.endDate,
        r.status,
        r.orderId ?? "",
        r.migrationSource ?? "",
      ]
        .map(escapeCsvValue)
        .join(","),
    );
  }
  return lines.join("\n");
}

function MultiSelectDropdown({
  id,
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className={styles.multiSelect} ref={containerRef}>
      <button
        type="button"
        id={id}
        className={styles.multiSelectTrigger}
        onClick={() => setOpen(prev => !prev)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{selected.length > 0 ? `${selected.length} selected` : "All"}</span>
        <span aria-hidden="true">&#9662;</span>
      </button>
      {open ? (
        <div
          className={styles.multiSelectDropdown}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
        >
          {options.map(option => (
            <label key={option} className={styles.multiSelectOption}>
              <input
                type="checkbox"
                className={styles.multiSelectCheckbox}
                checked={selected.includes(option)}
                onChange={() => {
                  if (selected.includes(option)) {
                    onChange(selected.filter(v => v !== option));
                  } else {
                    onChange([...selected, option]);
                  }
                }}
              />
              {option}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function BulkSubscriptionUpdatePage() {
  const { hasPermission } = usePermissions();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({
    startDate: null,
    endDate: null,
    siteStructureId: null,
    productCode: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [updateResult, setUpdateResult] = useState<{
    updated: number;
    errors: string[];
    notFound: string[];
  } | null>(null);
  const [dateErrors, setDateErrors] = useState<DateValidationError[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const ROWS_PER_PAGE = 25;
  const [showAllErrors, setShowAllErrors] = useState(false);

  const [activeTab, setActiveTab] = useState<"update" | "compare">("compare");
  const compareFileInputRef = useRef<HTMLInputElement>(null);
  const fulfilmentFileInputRef = useRef<HTMLInputElement>(null);
  const [comparePhase, setComparePhase] = useState<"idle" | "uploaded" | "comparing" | "done">(
    "idle",
  );
  const [compareFileName, setCompareFileName] = useState<string | null>(null);
  const [compareCrmText, setCompareCrmText] = useState<string | null>(null);
  const [fulfilmentCsvText, setFulfilmentCsvText] = useState<string | null>(null);
  const [fulfilmentFileName, setFulfilmentFileName] = useState<string | null>(null);
  const [fulfilmentIsDragging, setFulfilmentIsDragging] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [compareFulfilmentData, setCompareFulfilmentData] = useState<FulfilmentRow[]>([]);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [compareIsDragging, setCompareIsDragging] = useState(false);
  const [comparePage, setComparePage] = useState(0);
  const [compareFilters, setCompareFilters] = useState<CompareFilters>({
    region: [],
    countryName: [],
    productCode: [],
    status: [],
  });
  const [filterOptions, setFilterOptions] = useState<ExportFilterOptions | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filterLoadError, setFilterLoadError] = useState(false);

  const canEdit = hasPermission("fulfilment.edit_subscription");

  useEffect(() => {
    if (activeTab !== "compare" || filterOptions || filterLoadError) return;
    setFiltersLoading(true);
    get<ExportFilterOptions>("/v1/subscriptions/export/filters")
      .then(data => {
        const collator = new Intl.Collator(undefined, { sensitivity: "base" });
        setFilterOptions({
          regions: [...data.regions].sort(collator.compare),
          countries: [...data.countries].sort(collator.compare),
          productCodes: [...data.productCodes].sort(collator.compare),
          statuses: [...data.statuses].sort(collator.compare),
        });
      })
      .catch(err => {
        setFilterLoadError(true);
        setCompareError(
          `Failed to load filter options: ${err instanceof Error ? err.message : String(err)}`,
        );
      })
      .finally(() => setFiltersLoading(false));
  }, [activeTab, filterOptions, filterLoadError]);

  const reset = useCallback(() => {
    setFileName(null);
    setParseResult(null);
    setMapping({ startDate: null, endDate: null, siteStructureId: null, productCode: null });
    setDateErrors([]);
    setError(null);
    setProgress({ done: 0, total: 0 });
    setUpdateResult(null);
    setShowAllErrors(false);
    setPhase("idle");
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      reset();
      setFileName(file.name);
      setPhase("previewing");
      try {
        const result = parseCsv(await file.text());
        if (result.rows.length === 0) {
          setError("That file has a header but no data rows.");
          setPhase("idle");
          return;
        }
        setParseResult(result);
        setMapping(buildAutoMapping(result.headers));
        setPhase("mapping");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("idle");
      }
    },
    [reset],
  );

  const confirmMapping = useCallback(() => {
    if (!parseResult) return;

    const DATE_FIELD_KEYS: MappableFieldKey[] = ["startDate", "endDate"];
    const errors: DateValidationError[] = [];

    for (const row of parseResult.rows) {
      if (!row.valid) continue;
      const rowErrors: string[] = [];

      for (const key of DATE_FIELD_KEYS) {
        const colIdx = mapping[key];
        if (colIdx === null) continue;
        const value = row.cells[colIdx]?.trim();
        if (value && !isValidIsoDate(value)) {
          const header = parseResult.headers[colIdx];
          rowErrors.push(`${header} '${value}' is not a valid calendar date`);
        }
      }

      if (rowErrors.length > 0) {
        errors.push({ subscriptionId: row.subscriptionId, errors: rowErrors });
      }
    }

    setDateErrors(errors);
    setPhase("previewed");
  }, [parseResult, mapping]);

  const startRun = useCallback(async () => {
    if (!parseResult) return;
    const dateErrorIds = new Set(dateErrors.map(e => e.subscriptionId));
    const updates = buildUpdates(parseResult, mapping).filter(
      u => !dateErrorIds.has(u.subscriptionId),
    );
    if (updates.length === 0) return;

    setPhase("running");
    setProgress({ done: 0, total: updates.length });

    const result = await executeBulkUpdate(updates, (done, total) => {
      setProgress({ done, total });
    });
    setUpdateResult(result);
    setPhase("done");
  }, [parseResult, mapping, dateErrors]);

  const resetCompare = useCallback(() => {
    setCompareFileName(null);
    setCompareCrmText(null);
    setFulfilmentCsvText(null);
    setFulfilmentFileName(null);
    setComparisonResults([]);
    setCompareError(null);
    setComparePhase("idle");
    setComparePage(0);
    setCompareFilters({ region: [], countryName: [], productCode: [], status: [] });
    setFilterLoadError(false);
  }, []);

  const loadCompareFile = useCallback(async (file: File) => {
    setCompareError(null);
    setCompareFileName(file.name);
    try {
      const text = await file.text();
      setCompareCrmText(text);
      setComparePhase("uploaded");
    } catch (err) {
      setCompareError(err instanceof Error ? err.message : String(err));
      setComparePhase("idle");
    }
  }, []);

  const loadFulfilmentFile = useCallback(
    async (file: File) => {
      setCompareError(null);
      setFulfilmentFileName(file.name);
      try {
        const text = await file.text();
        setFulfilmentCsvText(text);
        if (compareCrmText) setComparePhase("uploaded");
      } catch (err) {
        setCompareError(err instanceof Error ? err.message : String(err));
      }
    },
    [compareCrmText],
  );

  const runComparison = useCallback(async () => {
    if (!compareCrmText) return;
    setComparePhase("comparing");
    setCompareError(null);
    try {
      let fulfilmentData: FulfilmentRow[];
      if (fulfilmentCsvText) {
        fulfilmentData = parseFulfilmentCsv(fulfilmentCsvText);
        if (fulfilmentData.length === 0) {
          setCompareError("No data rows found in the fulfilment CSV.");
          setComparePhase("uploaded");
          return;
        }
      } else {
        const params = new URLSearchParams();
        for (const v of compareFilters.region) params.append("region", v);
        for (const v of compareFilters.countryName) params.append("countryName", v);
        for (const v of compareFilters.productCode) params.append("productCode", v);
        for (const v of compareFilters.status) params.append("status", v);
        const qs = params.toString();
        const endpoint = `/v1/subscriptions/export${qs ? `?${qs}` : ""}`;
        fulfilmentData = await get<FulfilmentRow[]>(endpoint);
      }
      const crmRows = parseCrmCsv(compareCrmText);
      if (crmRows.length === 0) {
        setCompareError("No data rows found in the CRM CSV.");
        setComparePhase("uploaded");
        return;
      }
      const results = compareCrmWithFulfilment(crmRows, fulfilmentData);
      setComparisonResults(results);
      setCompareFulfilmentData(fulfilmentData);
      setComparePage(0);
      setComparePhase("done");
    } catch (err) {
      setCompareError(err instanceof Error ? err.message : String(err));
      setComparePhase("uploaded");
    }
  }, [compareCrmText, fulfilmentCsvText, compareFilters]);

  const handleUpdateMismatched = useCallback(() => {
    const mismatched = comparisonResults.filter(r => {
      if (!r.subscriptionId || r.assumed) return false;
      const hasDateMismatch = r.startDateMismatch || r.endDateMismatch;
      const hasAllowedProductChange =
        r.productMismatch && isBasicStandardChange(r.productCrm, r.productFulfilment);
      return hasDateMismatch || hasAllowedProductChange;
    });
    if (mismatched.length === 0) return;
    const csvText = buildMismatchCsv(mismatched);
    const file = new File([csvText], "mismatched-subscriptions.csv", { type: "text/csv" });
    setActiveTab("update");
    void loadFile(file);
  }, [comparisonResults, loadFile]);

  if (!canEdit) {
    return (
      <div className={styles.page}>
        <UnauthorizedView />
      </div>
    );
  }

  const isBusy = phase === "previewing" || phase === "running";
  const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  const mappedFieldCount = Object.values(mapping).filter(v => v !== null).length;
  const effectiveValidCount = parseResult ? parseResult.validCount - dateErrors.length : 0;
  const orderIdColIdx = parseResult
    ? parseResult.headers.findIndex(h => h.toLowerCase().replace(/[\s_-]+/g, "") === "crmorderid")
    : -1;
  const dateErrorIds = new Set(dateErrors.map(e => e.subscriptionId));
  const mappedFieldNames = MAPPABLE_FIELDS.filter(f => mapping[f.key] !== null)
    .map(f => `${f.label} → ${parseResult ? parseResult.headers[mapping[f.key] as number] : ""}`)
    .join(", ");

  const compareNotFound = comparisonResults.filter(r => r.matchType === "NOT FOUND").length;
  const compareMismatched = comparisonResults.filter(
    r =>
      r.matchType !== "NOT FOUND" &&
      (r.productMismatch || r.startDateMismatch || r.endDateMismatch),
  ).length;
  const compareMatched = comparisonResults.filter(
    r =>
      r.matchType !== "NOT FOUND" &&
      !r.productMismatch &&
      !r.startDateMismatch &&
      !r.endDateMismatch,
  ).length;

  const hasAnyFilter =
    compareFilters.region.length > 0 ||
    compareFilters.countryName.length > 0 ||
    compareFilters.productCode.length > 0 ||
    compareFilters.status.length > 0;

  const canCompare = compareCrmText !== null && (fulfilmentCsvText !== null || hasAnyFilter);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Subscription Audit"
        subtitle="Compare CRM orders against fulfilment subscriptions, or bulk update subscription dates, products, and regions from a CSV file"
      />

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "compare" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("compare")}
        >
          Fulfilment Export
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "update" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("update")}
        >
          Bulk Update
        </button>
      </div>

      {activeTab === "update" ? (
        <>
          {error ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {error}
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Upload</div>
            <div className={styles.dropHint} style={{ marginBottom: "var(--spacing-3)" }}>
              Upload a CSV containing subscription IDs and the new values you want to set. The file
              must have a <code>subscription_id</code> column with valid UUIDs. Map your CSV columns
              to the fields you want to update — only mapped fields will be changed. Rows with empty
              values for a mapped field are skipped for that field.
            </div>
            <div
              className={`${styles.drop} ${isDragging ? styles.dropActive : ""}`}
              onDragOver={event => {
                event.preventDefault();
                if (!isBusy) {
                  setIsDragging(true);
                }
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={event => {
                event.preventDefault();
                setIsDragging(false);
                if (isBusy) return;
                const dropped = event.dataTransfer.files[0];
                if (dropped) {
                  void loadFile(dropped);
                }
              }}
            >
              <div className={styles.dropText}>{fileName ?? "Drop a CSV here, or browse"}</div>
              <input
                ref={fileInputRef}
                type="file"
                aria-label="CSV of subscription updates"
                accept=".csv,.tsv,.txt,text/csv"
                className={styles.hiddenInput}
                disabled={isBusy}
                onChange={event => {
                  const chosen = event.target.files?.[0];
                  event.target.value = "";
                  if (chosen && !isBusy) {
                    void loadFile(chosen);
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
              >
                Browse
              </Button>
              <div className={styles.dropHint}>
                Must contain a <code>subscription_id</code> column (UUID) and at least one of{" "}
                <code>start_date</code>, <code>end_date</code>, <code>site_structure_id</code>, or{" "}
                <code>product_code</code>. Tab-separated files and extra columns are fine.
              </div>
            </div>
          </div>

          {parseResult && (phase === "mapping" || phase === "previewed") ? (
            <div className={styles.card}>
              <div className={styles.cardTitle}>Column mapping</div>

              {parseResult ? (
                <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
                  Loaded {fileName} — {parseResult.validCount} unique subscription
                  {parseResult.validCount === 1 ? "" : "s"} found
                  {parseResult.duplicateCount > 0
                    ? `, ${parseResult.duplicateCount} duplicate${parseResult.duplicateCount === 1 ? "" : "s"} removed`
                    : ""}
                  {parseResult.invalidCount > 0
                    ? `, ${parseResult.invalidCount} invalid row${parseResult.invalidCount === 1 ? "" : "s"} skipped`
                    : ""}
                  .
                </div>
              ) : null}

              <div className={styles.mappingGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>
                    subscription_id <span className={styles.required}>*</span>
                  </label>
                  <span className={styles.lockedField}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--color-success)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {parseResult.headers[parseResult.subscriptionIdIndex]}
                  </span>
                </div>

                {MAPPABLE_FIELDS.map((field, fieldIndex) => (
                  <div
                    key={field.key}
                    className={`${styles.field}${fieldIndex % 2 === 0 ? ` ${styles.fieldAlt}` : ""}`}
                  >
                    <label className={styles.label} htmlFor={`map-${field.key}`}>
                      {field.label}
                    </label>
                    <select
                      id={`map-${field.key}`}
                      className={styles.select}
                      disabled={phase !== "mapping"}
                      value={mapping[field.key] ?? ""}
                      onChange={event => {
                        const val = event.target.value;
                        setMapping(prev => ({
                          ...prev,
                          [field.key]: val === "" ? null : Number(val),
                        }));
                      }}
                    >
                      <option value="">-- not mapped --</option>
                      {parseResult.headers.map((header, idx) =>
                        idx === parseResult.subscriptionIdIndex ? null : (
                          <option key={idx} value={idx}>
                            {header}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                ))}
              </div>

              {phase === "mapping" ? (
                <>
                  <div className={styles.mappingHint}>
                    Select which CSV columns contain the new values to write to each subscription.
                    Map at least one field. Only mapped fields are updated — unmapped fields are
                    left unchanged. Rows with empty values for a mapped field are skipped for that
                    field.
                  </div>
                  <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                    <span className={styles.spacer} />
                    <Button variant="outlined" onClick={reset}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={confirmMapping}
                      disabled={!hasMappedField(mapping)}
                    >
                      Preview
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {parseResult && phase === "previewed" ? (
            <>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Preview changes</div>
                <div className={styles.statsSummary}>
                  {effectiveValidCount} valid &middot;{" "}
                  {parseResult.invalidCount + dateErrors.length} skipped &middot; {mappedFieldCount}{" "}
                  field{mappedFieldCount === 1 ? "" : "s"} mapped
                </div>

                <div className={styles.previewTableWrap}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th>subscription_id</th>
                        {orderIdColIdx !== -1 ? <th>crm_order_id</th> : null}
                        {MAPPABLE_FIELDS.filter(f => mapping[f.key] !== null).flatMap(f => {
                          const norm = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");
                          const fromIdx = f.currentHeaders.reduce<number>((found, candidate) => {
                            if (found !== -1) return found;
                            return parseResult.headers.findIndex(h => norm(h) === norm(candidate));
                          }, -1);
                          const ths = [];
                          if (fromIdx !== -1)
                            ths.push(<th key={`${f.key}-from`}>Current {f.label}</th>);
                          ths.push(<th key={f.key}>New {f.label}</th>);
                          return ths;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {parseResult.rows
                        .filter(r => r.valid && !dateErrorIds.has(r.subscriptionId))
                        .slice(previewPage * ROWS_PER_PAGE, (previewPage + 1) * ROWS_PER_PAGE)
                        .map(row => (
                          <tr key={row.lineNumber}>
                            <td>{row.subscriptionId}</td>
                            {orderIdColIdx !== -1 ? (
                              <td>{row.cells[orderIdColIdx] ?? ""}</td>
                            ) : null}
                            {MAPPABLE_FIELDS.filter(f => mapping[f.key] !== null).flatMap(f => {
                              const norm = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");
                              const fromIdx = f.currentHeaders.reduce<number>(
                                (found, candidate) => {
                                  if (found !== -1) return found;
                                  return parseResult.headers.findIndex(
                                    h => norm(h) === norm(candidate),
                                  );
                                },
                                -1,
                              );
                              const rawNewVal = row.cells[mapping[f.key] as number] ?? "";
                              const newVal =
                                f.key === "siteStructureId"
                                  ? resolveRegionDisplay(rawNewVal, filterOptions?.countries)
                                  : rawNewVal;
                              const oldVal = fromIdx !== -1 ? (row.cells[fromIdx] ?? "") : "";
                              const tds = [];
                              if (fromIdx !== -1)
                                tds.push(
                                  <td key={`${f.key}-from`} className={styles.cellCurrent}>
                                    {oldVal}
                                  </td>,
                                );
                              const noChange = !newVal || (fromIdx !== -1 && newVal === oldVal);
                              tds.push(
                                <td
                                  key={f.key}
                                  className={
                                    noChange
                                      ? styles.cellNoChange
                                      : newVal !== oldVal
                                        ? styles.cellChanged
                                        : undefined
                                  }
                                >
                                  {noChange ? "—" : newVal}
                                </td>,
                              );
                              return tds;
                            })}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {effectiveValidCount > ROWS_PER_PAGE ? (
                  <div
                    className={styles.toolbar}
                    style={{ marginTop: "var(--spacing-2)", justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      disabled={previewPage === 0}
                      onClick={() => setPreviewPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className={styles.mappingHint} style={{ margin: "0 var(--spacing-2)" }}>
                      Page {previewPage + 1} of {Math.ceil(effectiveValidCount / ROWS_PER_PAGE)} (
                      {effectiveValidCount} rows)
                    </span>
                    <Button
                      variant="outlined"
                      disabled={(previewPage + 1) * ROWS_PER_PAGE >= effectiveValidCount}
                      onClick={() => setPreviewPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                ) : null}
              </div>

              {dateErrors.length > 0 ? (
                <div className={`${styles.banner} ${styles.bannerWarning}`} role="status">
                  <div>
                    {dateErrors.length} row{dateErrors.length === 1 ? "" : "s"} skipped due to
                    invalid dates:
                  </div>
                  <ul style={{ margin: "var(--spacing-1) 0 0", paddingLeft: "var(--spacing-4)" }}>
                    {dateErrors.map(e => (
                      <li key={e.subscriptionId}>
                        {e.subscriptionId}: {e.errors.join("; ")}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className={`${styles.banner} ${styles.bannerInfo}`}>
                {effectiveValidCount} subscription
                {effectiveValidCount === 1 ? "" : "s"} loaded. Fields to update: {mappedFieldNames}.
                Rows with empty values for a mapped field will be skipped for that field.
              </div>

              <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                <span className={styles.spacer} />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setPhase("mapping");
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void startRun()}
                  disabled={effectiveValidCount === 0}
                >
                  Update {effectiveValidCount} subscription
                  {effectiveValidCount === 1 ? "" : "s"}
                </Button>
              </div>
            </>
          ) : null}

          {phase === "running" ? (
            <div className={styles.card}>
              <div className={styles.cardTitle}>Updating</div>
              <div className={styles.progressDetail}>
                Updating {progress.done} of {progress.total}...
              </div>
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${percent}%` }} />
              </div>
            </div>
          ) : null}

          {phase === "done" && updateResult ? (
            <>
              {updateResult.errors.length === 0 ? (
                <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
                  Successfully updated {updateResult.updated} subscription
                  {updateResult.updated === 1 ? "" : "s"}.
                </div>
              ) : (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
                  Completed with {updateResult.errors.length} error
                  {updateResult.errors.length === 1 ? "" : "s"}. {updateResult.updated} subscription
                  {updateResult.updated === 1 ? "" : "s"} updated successfully.
                </div>
              )}

              <div className={styles.card}>
                <div className={styles.cardTitle}>Complete</div>
                <div className={styles.statsSummary}>
                  {updateResult.updated} updated &middot; {updateResult.errors.length} error
                  {updateResult.errors.length === 1 ? "" : "s"}
                  {updateResult.notFound.length > 0
                    ? ` · ${updateResult.notFound.length} not found`
                    : ""}
                </div>
              </div>

              {updateResult.notFound.length > 0 ? (
                <div className={`${styles.banner} ${styles.bannerWarning}`} role="status">
                  <details className={styles.notFoundDetails}>
                    <summary>
                      {updateResult.notFound.length} subscription
                      {updateResult.notFound.length === 1 ? "" : "s"} not found
                    </summary>
                    <ul className={styles.notFoundList}>
                      {updateResult.notFound.map(id => (
                        <li key={id}>{id}</li>
                      ))}
                    </ul>
                  </details>
                </div>
              ) : null}

              {(showAllErrors ? updateResult.errors : updateResult.errors.slice(0, 3)).map(
                (errMsg, i) => (
                  <div key={i} className={`${styles.banner} ${styles.bannerError}`} role="alert">
                    {errMsg}
                  </div>
                ),
              )}
              {updateResult.errors.length > 3 && !showAllErrors ? (
                <Button variant="outlined" onClick={() => setShowAllErrors(true)}>
                  Show all {updateResult.errors.length} errors
                </Button>
              ) : null}

              <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                <Button variant="contained" onClick={reset}>
                  Upload another file
                </Button>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {activeTab === "compare" ? (
        <>
          {compareError ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {compareError}
            </div>
          ) : null}

          <div className={styles.dropHint} style={{ marginBottom: "var(--spacing-2)" }}>
            Upload a CRM order export from Dynamics to compare against fulfilment subscriptions —
            finding date mismatches, product mismatches, and missing subscriptions. Use the filters
            to select which fulfilment subscriptions to include — only subscriptions matching the
            selected region, country, product, and status will be returned.
          </div>

          <div className={styles.filterRow}>
            <div className={styles.filterField}>
              <label className={styles.label} htmlFor="filter-region">
                Region
              </label>
              <MultiSelectDropdown
                id="filter-region"
                label="Region"
                options={filterOptions?.regions ?? []}
                selected={compareFilters.region}
                onChange={values => setCompareFilters(prev => ({ ...prev, region: values }))}
                disabled={filtersLoading || !filterOptions}
              />
            </div>
            <div className={styles.filterField}>
              <label className={styles.label} htmlFor="filter-country">
                Country
              </label>
              <MultiSelectDropdown
                id="filter-country"
                label="Country"
                options={filterOptions?.countries ?? []}
                selected={compareFilters.countryName}
                onChange={values => setCompareFilters(prev => ({ ...prev, countryName: values }))}
                disabled={filtersLoading || !filterOptions}
              />
            </div>
            <div className={styles.filterField}>
              <label className={styles.label} htmlFor="filter-product">
                Product
              </label>
              <MultiSelectDropdown
                id="filter-product"
                label="Product"
                options={filterOptions?.productCodes ?? []}
                selected={compareFilters.productCode}
                onChange={values => setCompareFilters(prev => ({ ...prev, productCode: values }))}
                disabled={filtersLoading || !filterOptions}
              />
            </div>
            <div className={styles.filterField}>
              <label className={styles.label} htmlFor="filter-status">
                Status
              </label>
              <MultiSelectDropdown
                id="filter-status"
                label="Status"
                options={filterOptions?.statuses ?? []}
                selected={compareFilters.status}
                onChange={values => setCompareFilters(prev => ({ ...prev, status: values }))}
                disabled={filtersLoading || !filterOptions}
              />
            </div>
          </div>

          <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
            <Button
              variant="outlined"
              disabled={filtersLoading || !hasAnyFilter}
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  for (const v of compareFilters.region) params.append("region", v);
                  for (const v of compareFilters.countryName) params.append("countryName", v);
                  for (const v of compareFilters.productCode) params.append("productCode", v);
                  for (const v of compareFilters.status) params.append("status", v);
                  const qs = params.toString();
                  const data = await get<FulfilmentRow[]>(
                    `/v1/subscriptions/export${qs ? `?${qs}` : ""}`,
                  );
                  downloadCsv(fulfilmentToCsv(data), "fulfilment-export.csv");
                } catch (err) {
                  setCompareError(err instanceof Error ? err.message : String(err));
                }
              }}
            >
              Download fulfilment data
            </Button>
            {!hasAnyFilter ? (
              <span className={styles.mappingHint}>Select at least one filter to export</span>
            ) : null}
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>CRM Export</div>
            <div
              className={`${styles.drop} ${compareIsDragging ? styles.dropActive : ""}`}
              onDragOver={event => {
                event.preventDefault();
                if (comparePhase !== "comparing") setCompareIsDragging(true);
              }}
              onDragLeave={() => setCompareIsDragging(false)}
              onDrop={event => {
                event.preventDefault();
                setCompareIsDragging(false);
                if (comparePhase === "comparing") return;
                const dropped = event.dataTransfer.files[0];
                if (dropped) void loadCompareFile(dropped);
              }}
            >
              <div className={styles.dropText}>
                {compareFileName ?? "Drop a CRM export CSV here, or browse"}
              </div>
              <input
                ref={compareFileInputRef}
                type="file"
                aria-label="CRM export CSV"
                accept=".csv,.tsv,.txt,text/csv"
                className={styles.hiddenInput}
                disabled={comparePhase === "comparing"}
                onChange={event => {
                  const chosen = event.target.files?.[0];
                  event.target.value = "";
                  if (chosen && comparePhase !== "comparing") void loadCompareFile(chosen);
                }}
              />
              <Button
                variant="outlined"
                onClick={() => compareFileInputRef.current?.click()}
                disabled={comparePhase === "comparing"}
              >
                Browse
              </Button>
              <div className={styles.dropHint}>
                Upload a CRM export CSV to compare with fulfilment subscriptions.
              </div>
            </div>

            {comparePhase !== "idle" && comparePhase !== "comparing" ? (
              <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                <span className={styles.spacer} />
                <Button variant="outlined" onClick={resetCompare}>
                  Clear
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void runComparison()}
                  disabled={!canCompare}
                >
                  Compare
                </Button>
              </div>
            ) : null}
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>Fulfilment Export CSV (optional)</div>
            <div
              className={`${styles.drop} ${fulfilmentIsDragging ? styles.dropActive : ""}`}
              onDragOver={event => {
                event.preventDefault();
                if (comparePhase !== "comparing") setFulfilmentIsDragging(true);
              }}
              onDragLeave={() => setFulfilmentIsDragging(false)}
              onDrop={event => {
                event.preventDefault();
                setFulfilmentIsDragging(false);
                if (comparePhase === "comparing") return;
                const dropped = event.dataTransfer.files[0];
                if (dropped) void loadFulfilmentFile(dropped);
              }}
            >
              <div className={styles.dropText}>
                {fulfilmentFileName ?? "Drop a fulfilment export CSV here, or browse"}
              </div>
              <input
                ref={fulfilmentFileInputRef}
                type="file"
                aria-label="Fulfilment export CSV"
                accept=".csv,.tsv,.txt,text/csv"
                className={styles.hiddenInput}
                disabled={comparePhase === "comparing"}
                onChange={event => {
                  const chosen = event.target.files?.[0];
                  event.target.value = "";
                  if (chosen && comparePhase !== "comparing") void loadFulfilmentFile(chosen);
                }}
              />
              <Button
                variant="outlined"
                onClick={() => fulfilmentFileInputRef.current?.click()}
                disabled={comparePhase === "comparing"}
              >
                Browse
              </Button>
              <div className={styles.dropHint}>
                Upload a fulfilment export CSV (from prod \copy) to skip the API call. When
                provided, filters are ignored and the comparison runs against this file instead.
              </div>
            </div>
            {fulfilmentFileName ? (
              <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                <span className={styles.mappingHint}>{fulfilmentFileName}</span>
                <span className={styles.spacer} />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFulfilmentCsvText(null);
                    setFulfilmentFileName(null);
                  }}
                >
                  Clear
                </Button>
              </div>
            ) : null}
          </div>

          {comparePhase === "comparing" ? (
            <div className={styles.card}>
              <div className={styles.cardTitle}>Comparing</div>
              <div className={styles.progressDetail}>
                Fetching subscriptions and running comparison...
              </div>
            </div>
          ) : null}

          {comparePhase === "done" ? (
            <>
              <div className={`${styles.banner} ${styles.bannerInfo}`}>
                <strong>{compareMatched}</strong> matched &middot;{" "}
                <strong>{compareMismatched}</strong> mismatched &middot;{" "}
                <strong>{compareNotFound}</strong> not found
              </div>

              <div className={styles.card}>
                <div className={styles.cardTitle}>Comparison results</div>
                <div className={styles.dropHint} style={{ marginBottom: "var(--spacing-3)" }}>
                  The table below is a preview of the comparison. Each CRM order is matched against
                  fulfilment by order ID and product, then by company name, product and country,
                  then by name and country only. The comparison replaces fulfilment dates with CRM
                  dates where they differ. For product mismatches, only Basic↔Standard changes are
                  included — all other product differences are excluded. <strong>Matched</strong> —
                  dates and product agree. <strong>Mismatched</strong> — dates or product differ.{" "}
                  <strong>Not found</strong> — no fulfilment subscription found.{" "}
                  <strong>Assumed</strong> — matched by name and country only (product differs);
                  needs manual verification. Values in red indicate a mismatch.
                </div>
                <div className={styles.previewTableWrap}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th>subscription_id</th>
                        <th>match_type</th>
                        <th>assumed</th>
                        <th>firm (CRM)</th>
                        <th>firm (fulfilment)</th>
                        <th>country (CRM)</th>
                        <th>country (fulfilment)</th>
                        <th>product (CRM)</th>
                        <th>product (fulfilment)</th>
                        <th>product mismatch</th>
                        <th>start (CRM)</th>
                        <th>start (fulfilment)</th>
                        <th>start mismatch</th>
                        <th>end (CRM)</th>
                        <th>end (fulfilment)</th>
                        <th>end mismatch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonResults
                        .slice(comparePage * ROWS_PER_PAGE, (comparePage + 1) * ROWS_PER_PAGE)
                        .map((r, i) => (
                          <tr key={r.subscriptionId || `nf-${comparePage * ROWS_PER_PAGE + i}`}>
                            <td>{r.subscriptionId || "—"}</td>
                            <td
                              className={
                                r.matchType === "NOT FOUND" ? styles.cellMismatch : undefined
                              }
                            >
                              {r.matchType}
                            </td>
                            <td>{r.assumed ? "yes" : ""}</td>
                            <td>{r.firmNameCrm}</td>
                            <td>{r.firmNameFulfilment}</td>
                            <td>{r.countryCrm}</td>
                            <td>{r.countryFulfilment}</td>
                            <td className={r.productMismatch ? styles.cellMismatch : undefined}>
                              {r.productCrm}
                            </td>
                            <td className={r.productMismatch ? styles.cellMismatch : undefined}>
                              {r.productFulfilment}
                            </td>
                            <td className={r.productMismatch ? styles.cellMismatch : undefined}>
                              {r.productMismatch ? "yes" : ""}
                            </td>
                            <td className={r.startDateMismatch ? styles.cellMismatch : undefined}>
                              {r.crmStartDate}
                            </td>
                            <td className={r.startDateMismatch ? styles.cellMismatch : undefined}>
                              {r.fulfilmentStartDate}
                            </td>
                            <td className={r.startDateMismatch ? styles.cellMismatch : undefined}>
                              {r.startDateMismatch ? "yes" : ""}
                            </td>
                            <td className={r.endDateMismatch ? styles.cellMismatch : undefined}>
                              {r.crmEndDate}
                            </td>
                            <td className={r.endDateMismatch ? styles.cellMismatch : undefined}>
                              {r.fulfilmentEndDate}
                            </td>
                            <td className={r.endDateMismatch ? styles.cellMismatch : undefined}>
                              {r.endDateMismatch ? "yes" : ""}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {comparisonResults.length > ROWS_PER_PAGE ? (
                  <div
                    className={styles.toolbar}
                    style={{ marginTop: "var(--spacing-2)", justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      disabled={comparePage === 0}
                      onClick={() => setComparePage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className={styles.mappingHint} style={{ margin: "0 var(--spacing-2)" }}>
                      Page {comparePage + 1} of{" "}
                      {Math.ceil(comparisonResults.length / ROWS_PER_PAGE)} (
                      {comparisonResults.length} rows)
                    </span>
                    <Button
                      variant="outlined"
                      disabled={(comparePage + 1) * ROWS_PER_PAGE >= comparisonResults.length}
                      onClick={() => setComparePage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className={`${styles.toolbar} ${styles.toolbarBottom}`}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    downloadCsv(comparisonToCsv(comparisonResults), "crm-comparison.csv")
                  }
                >
                  Download comparison
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    downloadCsv(fulfilmentToCsv(compareFulfilmentData), "fulfilment-export.csv")
                  }
                >
                  Download fulfilment
                </Button>
                <span className={styles.spacer} />
                {compareMismatched > 0 ? (
                  <Button variant="contained" onClick={handleUpdateMismatched}>
                    Update {compareMismatched} mismatched
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
